import type { LucideIcon } from 'lucide-react'
import {
  BarChart3,
  BookOpenCheck,
  Building2,
  CalendarDays,
  ClipboardList,
  GraduationCap,
  Home,
  Inbox,
  MessageCircle,
  MessageSquare,
  Settings,
  ShieldCheck,
  Swords,
  UserCircle,
  Users2,
} from 'lucide-react'

import type { AuthRole } from '@/types/shared'

export type NavigationItem = {
  label: string
  description?: string
  href: string
  icon: LucideIcon
  exact?: boolean
  badgeCount?: number
  active?: boolean
}

export type NavigationSection = {
  title?: string
  items: NavigationItem[]
}

type RoleNavigationMap = Record<AuthRole, NavigationSection[]>

const navigationByRole: RoleNavigationMap = {
  player: [
    {
      items: [
        {
          label: 'ダッシュボード',
          href: '/player',
          icon: Home,
          exact: true,
        },
        {
          label: 'サッカー日記',
          href: '/player/soccer_diaries',
          description: '毎日の練習・試合を記録',
          icon: CalendarDays,
        },
        {
          label: 'チャット',
          href: '/player/chatrooms',
          description: '高校とのメッセージ履歴',
          icon: MessageCircle,
        },
        {
          label: '登録高校一覧',
          href: '/player/schools',
          description: '登録されている高校',
          icon: Building2,
        },
        {
          label: 'プロフィール',
          href: '/player/profile/edit',
          description: 'スカウトに公開される情報',
          icon: UserCircle,
        },
      ],
    },
  ],
  team: [
    {
      items: [
        {
          label: 'ホーム',
          href: '/team',
          icon: BarChart3,
          exact: true,
        },
        {
          label: '所属選手',
          href: '/team/players',
          icon: Users2,
        },
        {
          label: 'チャット',
          href: '/team/chatrooms',
          icon: MessageSquare,
        },
        {
          label: '学校からの問い合わせ',
          href: '/team/team_chatrooms',
          icon: Inbox,
        },
        {
          label: '承認リクエスト',
          href: '/team/profile_approvals',
          icon: ClipboardList,
        },
        {
          label: '練習試合',
          href: '/team/practice_matches',
          icon: Swords,
        },
        {
          label: '登録高校一覧',
          href: '/team/schools',
          icon: Building2,
        },
      ],
    },
  ],
  school: [
    {
      items: [
        {
          label: 'ダッシュボード',
          href: '/school',
          icon: Home,
          exact: true,
        },
        {
          label: '選手検索',
          href: '/school/players',
          icon: GraduationCap,
        },
        {
          label: '選手とのチャット',
          href: '/school/chatrooms',
          icon: Inbox,
        },
        {
          label: 'チームコーチとのチャット',
          href: '/school/team_chatrooms',
          icon: ShieldCheck,
        },
        {
          label: '練習試合',
          href: '/school/practice_matches',
          icon: Swords,
        },
        {
          label: '学校情報',
          href: '/school/school/edit',
          icon: Building2,
        },
      ],
    },
  ],
  admin: [
    {
      title: '管理',
      items: [
        {
          label: 'サマリー',
          href: '/admin/dashboard',
          icon: ShieldCheck,
        },
        {
          label: 'チーム',
          href: '/admin/teams',
          icon: Users2,
        },
        {
          label: '学校',
          href: '/admin/schools',
          icon: Building2,
        },
        {
          label: '選手',
          href: '/admin/players',
          icon: GraduationCap,
        },
        {
          label: '練習試合',
          href: '/admin/practice_matches',
          icon: Swords,
        },
        {
          label: '設定',
          href: '/admin/settings',
          icon: Settings,
        },
      ],
    },
    {
      title: 'コミュニケーション',
      items: [
        {
          label: 'お知らせ管理',
          href: '/admin/announcements',
          icon: BookOpenCheck,
        },
      ],
    },
  ],
}

export function buildNavigation(role: AuthRole | null, currentPath: string): NavigationSection[] {
  if (!role) {
    return []
  }

  return navigationByRole[role].map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      exact: item.exact ?? false,
      badgeCount: item.badgeCount ?? undefined,
      active: item.exact
        ? currentPath === item.href
        : currentPath === item.href || currentPath.startsWith(`${item.href}/`),
    })),
  }))
}
